/**
 * VideoCard.tsx — карточка видео (neobrutalism).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Trash2, Play, BarChart3, Globe2, Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { getStreamUrl, deleteVideo } from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { Video } from '../types';

interface VideoCardProps {
  video: Video;
  onDelete?: () => void;
}

const statusConfig = {
  processing: {
    label: 'Обрабатывается',
    badgeCls: 'bg-primary text-primary-foreground border-foreground',
    dotCls: 'bg-foreground',
    showDot: true,
  },
  ready: {
    label: 'Готово',
    badgeCls: 'bg-[#16A34A] text-white border-foreground',
    dotCls: '',
    showDot: false,
  },
  failed: {
    label: 'Ошибка',
    badgeCls: 'bg-destructive text-destructive-foreground border-foreground',
    dotCls: '',
    showDot: false,
  },
} as const;

export const VideoCard: React.FC<VideoCardProps> = ({ video, onDelete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleClick = () => {
    if (video.status === 'ready') navigate(`/watch/${video.id}`);
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteVideo(video.id);
      onDelete?.();
    } catch {
      setDeleteError('Unable to delete video.');
      setDeleting(false);
    }
  };

  const formattedDate = new Date(video.createdAt).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const thumbnailSrc = video.thumbnailUrl ? getStreamUrl(video.thumbnailUrl, video.visibility === 'private') : null;
  const cfg = statusConfig[video.status];
  const visibilityLabel = video.visibility === 'private' ? 'Private' : 'Public';

  return (
    <Card
      onClick={handleClick}
      className={cn(
        'overflow-hidden flex flex-col transition-all duration-150',
        video.status === 'ready'
          ? 'cursor-pointer hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg'
          : 'cursor-default'
      )}
    >
      {/* Превью */}
      <div className="aspect-video bg-muted relative border-b-2 border-foreground overflow-hidden group">
        {user && video.canDelete && (
          <Button
            variant="destructive"
            size="sm"
            className={cn(
              'absolute right-2 top-2 z-20 h-8 px-2 text-xs opacity-100 shadow-neo-sm transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg focus-visible:opacity-100 sm:pointer-events-none sm:opacity-0 sm:group-hover:pointer-events-auto sm:group-hover:opacity-100 sm:focus-visible:pointer-events-auto',
              deleting && 'pointer-events-auto opacity-100'
            )}
            onClick={handleDelete}
            disabled={deleting}
            aria-label={`Delete ${video.title}`}
            title="Delete video"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {deleting ? 'Deleting' : 'Delete'}
          </Button>
        )}

        {thumbnailSrc ? (
          <>
            <img
              src={thumbnailSrc}
              alt={video.title}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            {video.status === 'ready' && (
              <div className="absolute inset-0 flex items-center justify-center bg-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-primary border-2 border-foreground p-3 shadow-neo-sm">
                  <Play className="w-5 h-5 text-foreground fill-current" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {video.status === 'ready' ? (
              <Play className="w-8 h-8 text-foreground" />
            ) : video.status === 'processing' ? (
              <div className="w-8 h-8 border-2 border-t-transparent border-foreground rounded-full animate-spin" />
            ) : (
              <span className="text-destructive text-3xl font-black">!</span>
            )}
          </div>
        )}
      </div>

      <CardContent className="p-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className={cn('font-mono text-[10px] uppercase tracking-wider gap-1.5 border-2', cfg.badgeCls)}>
              {cfg.showDot && (
                <span className={cn('w-1.5 h-1.5 animate-pulse', cfg.dotCls)} />
              )}
              {cfg.label}
            </Badge>
            <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-wider gap-1.5 border-2 border-foreground bg-card">
              {video.visibility === 'private' ? <Lock className="w-3 h-3" /> : <Globe2 className="w-3 h-3" />}
              {visibilityLabel}
            </Badge>
          </div>
        </div>

        <h3 className="text-foreground font-bold text-sm leading-snug line-clamp-2 flex-1">
          {video.title}
        </h3>

        {video.error && (
          <p className="text-destructive text-xs font-mono truncate border-l-2 border-destructive pl-2" title={video.error}>
            {video.error}
          </p>
        )}
        {deleteError && (
          <p className="text-destructive text-xs font-mono border-l-2 border-destructive pl-2" role="alert">
            {deleteError}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 border-t-2 border-foreground">
          <div className="flex items-center gap-1.5 text-foreground/50 text-xs font-mono font-bold">
            <BarChart3 className="w-3 h-3" />
            <span>HLS · ABR</span>
          </div>
          <span className="text-foreground/50 text-xs font-mono">{formattedDate}</span>
        </div>
      </CardContent>
    </Card>
  );
};
